import * as amplitude from "@amplitude/analytics-react-native"
import { useNavigation, useRoute } from "@react-navigation/native"
import { LinearGradient } from "expo-linear-gradient"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Image, ScrollView, View } from "react-native"
import CircularProgress from "react-native-circular-progress-indicator/src/circularProgress/index"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import FilledButtonM from "../../common/components/button/FilledButtonM"
import {
    DiagnosticNavigationM,
    SwipeableWrapper,
} from "../../common/components/navigation/DiagnosticNavigationM"
import { SpeechBubbleM } from "../../common/components/speech-bubble/SpeechBubbleM"
import TextM from "../../common/components/text/TextM"
import { useApiStatus } from "../../common/hooks/useApiStatus"
import { theme } from "../../common/theme/theme"
import { DimensionsContext, isWebMobile } from "../../common/utils/mobileUtils"
import {
    apiIdle,
    apiRequest,
    apiRequestWithPrerequest,
} from "../../data/redux/actions/api"
import { schoolCardReportService } from "../../data/services/schoolCardReportService"
import { ChampionImageM } from "../../student/champions/ChampionImageM"
import EnterEmailModal from "../EnterEmailModal"
import {
    getColorMapping,
    getDiagnosticData,
    getMockDiagnosticData,
    getPageComment,
    getPageIcon,
} from "./DiagnosticHelpers"

function DiagnosticScreen(props) {
    const route = useRoute()
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)
    const hash = route.params?.hash || ""
    const page = Number(route?.params?.pageNumber) || 1
    const getMail = route?.params?.getMail || false
    const scrollViewRef = useRef(null)

    const isLoading = useMemo(
        () => !Object.keys(props.analysisData).length && !getMail,
        [props.analysisData]
    )

    const [modalVisible, setModalVisible] = useState(getMail)

    const data = useMemo(() => {
        return Object.keys(props.analysisData).length
            ? getDiagnosticData(props.analysisData)
            : getMockDiagnosticData()
    }, [props.analysisData])

    const pageData = data?.[page - 1]

    const colorMapping = getColorMapping()

    const width =
        dimensions.width > 800 ? dimensions.width / 2.2 : dimensions.width - 20

    const goToPrevPage = () => {
        if (page > 1) {
            navigation.navigate("school-card-report/diagnostic", {
                pageNumber: page - 1,
                hash,
            })
        }
    }

    const goToNextPage = () => {
        if (page < data.length) {
            navigation.navigate("school-card-report/diagnostic", {
                pageNumber: page + 1,
                hash,
            })
        }

        if (page === data.length) {
            navigation.navigate("school-card-report/cta")
        }
    }

    useEffect(() => {
        if (
            (!Object.keys(props.analysisData).length ||
                hash !== props?.analysisData?.school_card_report?.hash) &&
            !getMail
        ) {
            props.apiRequestWithPrerequest(
                schoolCardReportService.getAnalysisDataByHash,
                {
                    hash,
                }
            )
        }
    }, [props.analysisData])

    useApiStatus(
        schoolCardReportService.getAnalysisDataByHash,
        null,
        false,
        () => {}
    )

    useEffect(() => {
        if (scrollViewRef.current) {
            scrollViewRef.current.scrollTo({ x: 0, y: 0, animated: true })
        }
    }, [page])

    const getLoadingScreen = () => {
        return (
            <LinearGradient
                colors={pageData.colors}
                style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <ActivityIndicator size="large" color={theme.colors.primary} />
            </LinearGradient>
        )
    }

    useEffect(() => {
        amplitude.track(`school_card_report.diagnostic_screen_${page}`)
    }, [page])

    return isLoading ? (
        getLoadingScreen()
    ) : (
        <SwipeableWrapper
            onSwipeLeft={() => {
                amplitude.track(
                    `school_card_report.diagnostic_screen_${page}.swipe_left`
                )
                goToNextPage()
            }}
            onSwipeRight={() => {
                amplitude.track(
                    `school_card_report.diagnostic_screen_${page}.swipe_right`
                )
                goToPrevPage()
            }}
            colors={pageData.colors}
            style={{
                flex: 1,
                ...(isWebMobile() && { minHeight: "100dvh" }),
            }}
        >
            <ScrollView
                ref={scrollViewRef}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                style={{ flex: 1, width: "100%" }}
                contentContainerStyle={{
                    flexGrow: 1,
                    alignItems: "center",
                }}
            >
                <LinearGradient
                    colors={pageData.colors}
                    style={{
                        flex: 1,
                        paddingTop: 20,
                        width: "100%",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <EnterEmailModal
                        modalVisible={modalVisible}
                        setModalVisible={setModalVisible}
                    />

                    <View
                        style={{
                            width: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Image
                            source={getPageIcon(pageData)}
                            style={{
                                width: (width * 2) / 5 + 20,
                                height: (width * 2) / 5 + 20,
                                resizeMode: "contain",
                                marginTop: 8,
                                marginBottom: 20,
                                shadowColor: "#000",
                                shadowOffset: { width: 0, height: 4 },
                                shadowOpacity: 0.2,
                                shadowRadius: 4,
                                elevation: 8,
                            }}
                        />

                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                width,
                            }}
                        >
                            <TextM
                                fontSize={32}
                                fontWeight="ExtraBold"
                                color={theme.colors.primary}
                                style={{
                                    textAlign: "center",
                                    flex: 1,
                                    marginBottom: 8,
                                }}
                                wrap
                            >
                                {pageData?.title.toUpperCase()}
                            </TextM>
                        </View>

                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                height: width / 2 - 20,
                                maxHeight: 80,
                                maxWidth: width,
                            }}
                        >
                            <TextM
                                fontSize={24}
                                fontWeight="ExtraBold"
                                color={
                                    colorMapping["inActiveStrokeColor"][
                                        pageData?.score
                                    ]
                                }
                                style={{
                                    textAlign: "center",
                                    marginRight: pageData?.isPillar ? 8 : 0,
                                }}
                                wrap={true}
                            >
                                {pageData?.isPillar
                                    ? pageData?.subtitle[pageData?.score]
                                    : pageData?.subtitle}
                            </TextM>

                            {pageData?.isPillar && (
                                <CircularProgress
                                    value={pageData?.score * 25}
                                    radius={40}
                                    title={`${pageData?.score}/4`}
                                    titleStyle={{
                                        fontSize: 16,
                                        fontWeight: "bold",
                                    }}
                                    activeStrokeColor={
                                        colorMapping["activeStrokeColor"][
                                            pageData?.score
                                        ]
                                    }
                                    inActiveStrokeColor={
                                        colorMapping["inActiveStrokeColor"][
                                            pageData?.score
                                        ]
                                    }
                                    strokeWidth={8}
                                    showProgressValue={false}
                                />
                            )}
                        </View>
                    </View>

                    <View style={{ flex: 1 }} />

                    <View
                        style={{
                            width: "100%",
                            paddingHorizontal: 20,
                            gap: 20,
                            marginBottom: 20,
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                width,
                                marginTop: 4,
                                marginBottom: 8,
                            }}
                        >
                            <ChampionImageM
                                name={pageData?.champion}
                                variant={
                                    pageData?.championVariant[pageData?.score]
                                }
                                width={80}
                            />

                            <View style={{ flex: 1, marginLeft: 10 }}>
                                <SpeechBubbleM
                                    texts={[getPageComment(pageData)]}
                                    position="left"
                                    hideIcons={true}
                                    noAnimation={true}
                                    containerStyle={{
                                        flex: 1,
                                    }}
                                    textStyle={{
                                        fontSize: 16,
                                        fontFamily: "Montserrat-Regular",
                                        color: theme.colors.black,
                                    }}
                                    numberOfSpeechLines={4}
                                />
                            </View>
                        </View>

                        <FilledButtonM
                            onPress={() =>
                                navigation.navigate({
                                    name: pageData.seeMore,
                                    params: { pageNumber: page },
                                })
                            }
                            trackingId={`school_card_report.diagnostic_screen_${page}.see_more`}
                            label={pageData?.buttonText || "Voir plus"}
                        />

                        <View
                            style={{
                                flexDirection: "row",
                                position: "relative",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: 4,
                            }}
                        >
                            <DiagnosticNavigationM
                                style={{
                                    width,
                                    alignSelf: "center",
                                    position: "absolute",
                                }}
                                currentIndex={Number(page) - 1}
                                url="/school-card-report/diagnostic"
                                data={data}
                                onPressLeft={() => {
                                    amplitude.track(
                                        `school_card_report.diagnostic_screen_${page}.press_left`
                                    )
                                    goToPrevPage()
                                }}
                                onPressRight={() => {
                                    amplitude.track(
                                        `school_card_report.diagnostic_screen_${page}.press_right`
                                    )
                                    goToNextPage()
                                }}
                            />

                            {Array.from({
                                length: data.length + 1,
                            }).map((_, index) => (
                                <View
                                    key={index}
                                    style={{
                                        width: index === page - 1 ? 20 : 10,
                                        height: 10,
                                        borderRadius: 5,
                                        marginHorizontal: 5,
                                        backgroundColor: theme.colors.primary,
                                        opacity: index === page - 1 ? 1 : 0.5,
                                        transition: "width 0.3s ease",
                                    }}
                                />
                            ))}
                        </View>
                    </View>
                </LinearGradient>
            </ScrollView>
        </SwipeableWrapper>
    )
}

const mapStateToProps = (state) => ({
    reportId: state.data.schoolCardReport?.id,
    hash: state.data.schoolCardReport?.hash,
    analysisData: state.data.schoolCardReport?.analysisData || {},
    schoolSubjects: state.data.staticData?.school_subjects,
})

const mapDispatchToProps = {
    apiRequest,
    apiRequestWithPrerequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(DiagnosticScreen)
